<template lang="pug">
include ../pug/svg
div#settings(v-if="course")
	Form.wrapper(ref="settingsForm" :validation-schema="settingsSchema" v-slot="{ values, errors, meta }")
		div.c1
			div.field.media.hero(:class="{selected:course.media||course.mediaEmbedUrl}")
				figure
					picture
						img(v-if="course.media" :src="course.media.mediaUrl" :alt="course.media.seoAlt")
						img(v-else-if="course.mediaEmbedUrl" :src="course.mediaEmbedUrl")
					div.butdbl.text(:class="{unselected : !course.media && !course.mediaEmbedUrl}")
						button.but(type="button" @click="selectMedia()") Media
						button.but(type="button" @click="removeMedia()")
							span Remove Media
							+svg(svg-filename="iconBin")
						button.but(type="button" @click="selectEmbed()") Embed
					figcaption.label Hero Image
			div.field
				Field(name="publishStatus" as="select" placeholder="Unsupplied" autocomplete="off" v-model="publishStatus" :required="true" :validateOnInput="false" v-slot="{value}" @change="enableCheck")
					option(v-for="(status, index) in filteredCourseStatuses" :value="status.value" :selected="status&&status.value===publishStatus") {{status.option}}
				label {{terminology('course','course','singular')}} / Status
				+svg(svg-filename="iconArrow" aria-hidden="true" alt="Arrow icon")
			
			FieldBoolean(name="certification" v-model="certification" :label="terminology('course','course','singular')+' / Certification'" yesLabel="On" noLabel="Off" @fieldUpdated="enableCheck")

		div.c2
			FieldText.grow(name="name" :label="terminology('course','course','singular')+' / Name'" v-model="name" :required="true" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" @blur="enableCheck")
			
			FieldText.grow(name="slug" :label="terminology('course','course','singular')+' / Slug'" v-model="slug" :disabled="true" :required="false" :cancelled="cancelled" :errors="errors" @keyup="enableCheck" @blur="enableCheck")
			
			FieldBoolean(name="lessonChronological" v-model="lessonChronological" :label="terminology('course','lesson','singular')+' / Access'" yesLabel="Chronological" noLabel="Nonchronological" @fieldUpdated="changeChronological")
				
			FieldBoolean(name="lessonAllowReturn" v-model="lessonAllowReturn" :label="terminology('course','lesson','singular')+' / Return to previous'" yesLabel="Allow" noLabel="Prevent" :disabled="!this.lessonChronological" @fieldUpdated="enableCheck")
			
			//-FieldBoolean(v-model="examMode" :label="terminology('course','course','singular')+' / Exam mode'" yesLabel="On" noLabel="Off" @fieldUpdated="changeExamMode")
			FieldBoolean(name="examMode" v-model="examMode" :label="terminology('course','course','singular')+' / Exam mode'" yesLabel="On" noLabel="Off" @fieldUpdated="changeExamMode")
			
			FieldBoolean(name="courseAllowRetake" v-model="courseAllowRetake" :label="allowRetakeLabel" yesLabel="Yes" noLabel="No" @fieldUpdated="enableCheck" :disabled="examMode?false:true")
				
			FieldText(name="examPassScore" :placeholder="examMode?'Unsupplied':'N/A'" :label="terminology('course','course','singular')+' / Exam Pass Score'" v-model="examPassScore" :required="examMode?true:false" :disabled="examMode?false:true" :cancelled="cancelled" append="Points" :numeric="true" :maxlength="4" :errors="errors" @keyup="enableCheck" @blur="enableCheck")
			
			FieldBoolean(name="examShowAllocation" v-model="examShowAllocation" label="Tasks / Display point allocation" :yesLabel="!examMode?'N/A':'Yes'" :noLabel="!examMode?'N/A':'No'" @fieldUpdated="enableCheck" :disabled="examMode?false:true")
			
			//-FieldBoolean(name="mentorGranularAccess" v-model="mentorGranularAccess" :label="terminology('user','mentor','plural')+ ' result access'" :yesLabel="!examMode?'N/A':'Result + Score + Answers'" :noLabel="!examMode?'N/A':'Result + Score'" @fieldUpdated="enableCheck" :disabled="examMode?false:true")
				
			//-FieldBoolean(name="mentorCompleteCourse" v-model="mentorCompleteCourse" :label="terminology('user','mentor','plural')+ ' Must complete course'" yesLabel="Yes" noLabel="No" @fieldUpdated="enableCheck" :disabled="examMode?false:true")
			
	MediaModal(mediaType="image" :multiple="false" @mediaInsert="mediaInsert" @embedInsert="embedInsert")
</template>

<script>
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import FieldText from '../components/FieldText';
import FieldBoolean from '../components/FieldBoolean';

import MediaModal from '../components/MediaModal';
import { mapFields } from 'vuex-map-fields';
import { createHelpers } from 'vuex-map-fields';

const { mapFields: mapCourseFields } = createHelpers({
	getterType: 'courses/getCourseField',
	mutationType: 'courses/updateCourseField',
});

export default {
	name: 'ManageLearningCourseSettings',
	components: {
		Form,
		Field,
		FieldText,
		FieldBoolean,
		MediaModal,
	},
	props: ['course', 'cancelled'],
	emits: ['storeUpdated'],
	data () {
		const req = 'Required';
		const settingsSchema = Yup.object().shape({
			name: Yup.string().required(req),
			examMode: Yup.number().required(req), // 1 or 0
			examPassScore: Yup.number().when(['examMode'], {
				is: (examMode) => examMode,
				then: Yup.number().typeError('Number required').required(req),
				otherwise: Yup.number().nullable(),
			}),
		});
		
		return {
			settingsSchema,
		};
	},
	computed: {
		...mapCourseFields([ // uses custom getter: getCourseField
			'name',
			'slug',
			'media',
			'mediaEmbedUrl',
			'publishStatus',
			'courseAllowRetake',
			'lessonAllowReturn',
			'lessonChronological',
			//'maxPlaces',
			//'publishStatus',
			//'seoDescription',
			'examMode',
			'examPassScore',
			'examShowAllocation',
			//'mentorGranularAccess',
			//'mentorCompleteCourse',
			'certification',
		]),
		...mapFields('courses', [ // get/set vuex store
			'courseStatuses',
		]),
		...mapFields('gui', [ // get/set vuex store
			'showMediaModal',
			'showEmbedModal',
			'tintState',
		]),
		filteredCourseStatuses() {
			const lessonCount = this.$store.getters['courses/getCourseLessonCount'](this.course.id);
			return (lessonCount) ? this.courseStatuses : this.courseStatuses.filter(s => s.value !== 'live');
		},
		allowRetakeLabel() {
			let label = this.terminology('course','course','singular');
			
			label += (this.examMode) ? ' / Allow Retake' : ' / Allow Repeat Access';
			
			return label;
		},
	},
	watch: {
		course: {
			handler(course) {
				this.$store.dispatch('gui/setHeader', {
					title: course.name,
					backRoute: {
						text: this.terminology('course','course','plural'),
						name: 'ManageLearning',
					},
				});		
			},
			deep: false,
		},
	},
	methods: {
		changeChronological() {
			if (!this.lessonChronological) {
				this.lessonAllowReturn = 1;
			}
			this.enableCheck();
		},
		changeExamMode() {
			if (!this.examMode) {
				this.examPassScore = null;
				this.courseAllowRetake = 1;
				this.examShowAllocation = 0;
//				this.mentorGranularAccess = 0;
			} else {
				this.examPassScore = 0;
			}
			this.enableCheck();
		},
		enableCheck() {
			const form = this.$refs.settingsForm;
			const {valid} = form.getMeta();
			
			this.$emit('storeUpdated', {
				screen: 'settings',
				valid,
			});
		},	
		selectMedia() {
			this.tintState = true;
			this.showMediaModal = true;
		},
		selectEmbed() {
			this.tintState = true;
			this.showEmbedModal = true;
		},
		mediaInsert(media) {
			this.media = media;
			this.mediaEmbedUrl = null;
			this.enableCheck();
		},
		embedInsert(embed) {
			this.media = null;
			this.mediaEmbedUrl = embed.mediaUrl;				
			this.enableCheck();			
		},
		removeMedia() {
			this.media = null;
			this.mediaEmbedUrl = null;
			this.enableCheck();
		},
		terminology(groupKey, termKey, varKey) {
			return this.$store.getters['gui/customTerm'](groupKey, termKey, varKey);
		},
	},
}
</script>

<style lang="scss">
	div#settings {
		width: 100%;
		form.wrapper {
			display: flex;
			margin-top: 30px;
			.c2 {
				flex-grow: 1;
				display: flex;
				flex-wrap: wrap;
				justify-content: space-between;
				.field {
					width: calc(50% - 5px);
				}
			}
		}
		@media only screen and (max-width:599px) {
			form.wrapper {
				.c2 {
					.field {
						max-width: none;
						width: 100%;
					}
				}
			}
		}
		@media only screen and (max-width:899px) {
			form.wrapper {
				flex-direction: column;
				.field {
					&.media {
						max-width: 600px;
						margin-left: auto;
						margin-right: auto;
					}
				}
			}
		}
		@media only screen and (min-width:900px) {
			form.wrapper {
				.c1 {
					width: 410px;
					min-width: 410px;
					margin-right: 20px;
				}
			}
		}
	}
	.field {
		&.media {
			padding: 0;
			overflow: hidden;
			.label {
				position: absolute;
				top: 10px;
				left: 10px;
			}
			&.selected {
				.label {
					color: #FFF;
				}
			}
			picture {
				aspect-ratio: 16/9;
				display: block;
				margin: 0;
				background: url(/img/icon-photo.svg) center no-repeat;
				background-size: 30px 30px;
				&:after {
//					z-index: 1;
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background: rgba(#FFF, 0.5);
					transition: opacity 0.3s ease-in-out;
					opacity: 0;
				}
			}
			.butdbl {
				z-index: 2;
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				pointer-events: none;				
				opacity: 0;
			}
			&:hover {
				.butdbl,
				picture:after {
					pointer-events: auto;
					opacity: 1;
				}
			}
		}
	}
</style>
